import React, { useCallback } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { EXPRESSION_LABELS } from "../../../constants/expressionBuilder.constants";
import "../../../styles/ExpressionBuilder.css";
import { createNewGroup, createNewRule } from "../../../utils/expressionBuilder.utils";

/**
 * Component for rendering group action buttons (Add Rule, Add Group)
 * @param {Object} group - Group configuration
 * @param {Function} onUpdate - Group update handler
 * @param {Array} availableFields - Available workflow variables
 * @param {boolean} isRoot - Whether this is the root expression group
 * @returns {JSX.Element} Group actions component
 * @author Abhishek Dhillon
 * @parent GroupComponent
 */
const GroupActions = ({ group, onUpdate, availableFields = [], isRoot = false }) => {
  /**
   * Updates group with new children
   * @param {Object} newProps - New properties
   * @author Abhishek Dhillon
   */
  const updateGroup = useCallback(
    (newProps) => {
      onUpdate({ ...group, ...newProps });
    },
    [group, onUpdate]
  );

  /**
   * Adds a new rule to the group
   * @author Abhishek Dhillon
   */
  const handleAddRule = useCallback(() => {
    const newRule = createNewRule(availableFields);
    updateGroup({ children: [...(group?.children || []), newRule] });
  }, [group?.children, updateGroup, availableFields]);

  /**
   * Adds a new group to the current group
   * @author Abhishek Dhillon
   */
  const handleAddGroup = useCallback(() => {
    const newGroup = createNewGroup(availableFields);
    updateGroup({ children: [...(group?.children || []), newGroup] });
  }, [group?.children, updateGroup, availableFields]);

  return (
    <div className='expression-builder-group-actions justify-content-center'>
      {/* Root expression can only add groups */}
      {isRoot && (
        <Button onClick={handleAddGroup} fillMode='outline' id={`add-group-${group?.id}`} icon={"plus"} className='group-actions-button'>
          {EXPRESSION_LABELS.ADD_GROUP}
        </Button>
      )}

      {/* Non-root expressions can only add rules */}
      {!isRoot && (
        <Button onClick={handleAddRule} icon='plus' themeColor='primary' fillMode='flat' id={`add-rule-${group?.id}`}>
          {EXPRESSION_LABELS.ADD_RULE}
        </Button>
      )}
    </div>
  );
};

export default GroupActions;
